import React, { useCallback, useEffect, useState } from "react";
import { message } from "antd";
import { Footer, LoadingIcon, Sidebar } from "../../../components";
import BarChart from "./BarChart";
import LineChart from "./LineChart";
import Dropdowns from "./Dropdowns";
import { getGraphData } from "../../../utils/api";

const DEFAULT_PAGE = 1;
const DEFAULT_NUMBARS = 10;

const BreederDashboard = () => {
  // State
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [siteMetadata, setSiteMetadata] = useState({});
  const [graphData, setGraphData] = useState({});
  const [currentOrg, setCurrentOrg] = useState();
  const [currentSite, setCurrentSite] = useState();
  const [currentCrop, setCurrentCrop] = useState();
  const [currentMetric, setCurrentMetric] = useState(null);
  const [metricType, setMetricType] = useState(null);
  const [ripenessData, setRipenessData] = useState({});
  const [barData, setBarData] = useState([]);
  const [lineData, setLineData] = useState({});
  const [barStartDate, setBarStartDate] = useState(null);
  const [barEndDate, setBarEndDate] = useState(null);
  const [graphLoading, setGraphLoading] = useState(false);
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [numBars, setNumBars] = useState(DEFAULT_NUMBARS);
  const [isCumulative, setIsCumulative] = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState(false);

  const [messageApi, contextHolder] = message.useMessage();

  const isDict = (v) =>
    typeof v === "object" && v !== null && !Array.isArray(v);
  const getMetricValue = (m) => (isDict(m) ? m.value : m);

  // Fetch graph data
  const fetchGraphData = useCallback(async () => {
    setGraphLoading(true);
    try {
      if (!currentOrg || !currentSite?.id || !currentCrop?.id) {
        setGraphData({});
        setBarStartDate(null);
        setBarEndDate(null);
        return;
      }
      const res = await getGraphData(
        currentOrg,
        currentSite.id,
        currentCrop.id,
        ripenessData,
      );
      const data = res?.data?.data || {};
      setGraphData(data);

      // Default date range
      const allDates = [
        ...new Set(
          Object.values(data)
            .flat()
            .map((x) => x?.harvest_date)
            .filter(Boolean),
        ),
      ].sort();
      setBarStartDate(allDates[0] || null);
      setBarEndDate(allDates[allDates.length - 1] || null);
    } catch (err) {
      messageApi.error(
        err?.response?.data?.message || "Oops! Error fetching graph data!",
      );
      setGraphData({});
      setBarStartDate(null);
      setBarEndDate(null);
    } finally {
      setGraphLoading(false);
    }
  }, [currentOrg, currentSite, currentCrop, ripenessData, messageApi]);

  // Update bar chart data
  useEffect(() => {
    if (
      !getMetricValue(currentMetric) ||
      !graphData ||
      !barStartDate ||
      !barEndDate
    ) {
      setBarData([]);
      return;
    }
    // Keep date-sorted copy
    const sorted = Object.fromEntries(
      Object.entries(graphData).map(([plot, arr]) => [
        plot,
        [...arr].sort((a, b) =>
          (a.harvest_date || "").localeCompare(b.harvest_date || ""),
        ),
      ]),
    );
    const data = Object.entries(sorted).map(([plot, arr]) => {
      const withinDates = arr.filter(
        (d) =>
          d?.harvest_date &&
          barStartDate &&
          barEndDate &&
          new Date(barStartDate) <= new Date(d.harvest_date) &&
          new Date(d.harvest_date) <= new Date(barEndDate),
      );
      let value = withinDates.reduce(
        (acc, d) => acc + (d?.data?.[getMetricValue(currentMetric)] ?? 0),
        0,
      );
      if (metricType === "avg" && withinDates.length)
        value /= withinDates.length;
      return { label: plot, val: value };
    });
    setBarData(data);
  }, [currentMetric, metricType, graphData, barStartDate, barEndDate]);

  useEffect(() => {
    setPage(1);
  }, [numBars]);

  // Update line chart data
  useEffect(() => {
    if (!getMetricValue(currentMetric) || !graphData) {
      setLineData({});
      return;
    }
    // Global ordered date list
    const dateList = [
      ...new Set(
        Object.values(graphData)
          .flat()
          .map((d) => d?.harvest_date)
          .filter(Boolean),
      ),
    ].sort();

    // Plots for current bar-page
    const left = numBars * (page - 1);
    const right = Math.min(numBars * page, Object.keys(graphData).length);
    const plots = Object.entries(graphData).slice(left, right);

    const cumulAllowed = !!siteMetadata?.cumulative_metrics?.includes(
      getMetricValue(currentMetric),
    );

    const lines = plots.map(([plotName, rawArr]) => {
      const arrSorted = [...rawArr].sort((a, b) =>
        (a.harvest_date || "").localeCompare(b.harvest_date || ""),
      );
      const dateToVal = Object.fromEntries(
        arrSorted.map((d) => [
          d.harvest_date,
          d.data?.[getMetricValue(currentMetric)],
        ]),
      );
      let runningTotal = 0;
      const aligned = dateList.map((dt) => {
        const raw = dateToVal[dt];
        if (cumulAllowed && isCumulative) {
          if (raw != null) runningTotal += raw;
          return raw != null ? runningTotal : null;
        }
        return raw ?? null;
      });
      return { name: plotName, val: aligned };
    });

    setLineData({ labels: dateList, data: lines });
  }, [currentMetric, graphData, page, numBars, siteMetadata, isCumulative]);

  // Auto-reset filters when currentOrg/site/crop changes
  useEffect(() => {
    setBarData([]);
    setLineData({});
    setBarStartDate(null);
    setBarEndDate(null);
  }, [currentOrg, currentSite, currentCrop]);

  return (
    <>
      {contextHolder}
      <Sidebar active="home" open={sidebarOpen} setOpen={setSidebarOpen} />
      <div id="dashboard">
        <div
          className={`flex flex-col w-full px-20 ${
            sidebarOpen ? "pl-60" : ""
          } pt-10 md:pt-4 pb-14 md:pl-15 md:pr-8 sm:pr-10 justify-center transition-padding ease-in-out duration-200`}
        >
          <Dropdowns
            setSiteMetadata={setSiteMetadata}
            currentMetric={currentMetric}
            setCurrentMetric={setCurrentMetric}
            metricType={metricType}
            setMetricType={setMetricType}
            currentOrg={currentOrg}
            setCurrentOrg={setCurrentOrg}
            currentSite={currentSite}
            setCurrentSite={setCurrentSite}
            currentCrop={currentCrop}
            setCurrentCrop={setCurrentCrop}
            fetchGraphData={fetchGraphData}
            setDropdownLoading={setDropdownLoading}
          />
          {Object.keys(siteMetadata).length === 0 && !dropdownLoading ? (
            <div className="text-2xl text-center">Metadata not Configured!</div>
          ) : graphLoading ? (
            <LoadingIcon classname="absolute top-0 left-0" height="200px" />
          ) : (
            <>
              {barData?.length > 0 && (
                <BarChart
                  barData={barData}
                  currentMetric={currentMetric}
                  metricType={metricType}
                  startDate={barStartDate}
                  endDate={barEndDate}
                  setStartDate={setBarStartDate}
                  setEndDate={setBarEndDate}
                  page={page}
                  setPage={setPage}
                  numBars={numBars}
                  setNumBars={setNumBars}
                  dataLen={Object.entries(graphData).length}
                  metricUnit={
                    currentSite?.metadata?.[currentCrop?.name]?.units?.[
                      getMetricValue(currentMetric)
                    ]
                      ? `(${
                          currentSite?.metadata?.[currentCrop?.name]?.units?.[
                            getMetricValue(currentMetric)
                          ]
                        })`
                      : ""
                  }
                />
              )}
              {lineData?.data?.length > 0 && (
                <LineChart
                  lineData={lineData}
                  currentMetric={currentMetric}
                  siteMetadata={siteMetadata}
                  isCumulative={isCumulative}
                  setIsCumulative={setIsCumulative}
                />
              )}
            </>
          )}
        </div>
        <Footer sidebarOpen={sidebarOpen} />
      </div>
    </>
  );
};

export default BreederDashboard;
